import React, { useState, useEffect, useMemo } from 'react';
import { GitBranchIcon, SparklesIcon, ZapIcon, TrophyIcon, Loader2Icon } from 'lucide-react';

const MultiverseProgress = ({ isActive, onPhaseChange }) => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [subPhase, setSubPhase] = useState(0);
  const [dots, setDots] = useState('');

  // Define the phases of multiverse exploration
  const phases = useMemo(() => [
    {
      id: 'initial',
      title: 'Initial Battle',
      description: 'All 3 AIs responding to your prompt',
      icon: ZapIcon,
      color: 'cyan',
      subPhases: [
        'Summoning GPT...',
        'Summoning Claude...',
        'Summoning Gemini...',
        'Analyzing initial responses...'
      ]
    },
    {
      id: 'universe-a',
      title: 'Universe A',
      description: 'GPT eliminated first',
      icon: GitBranchIcon,
      color: 'purple',
      subPhases: [
        'Claude & Gemini critiquing GPT...',
        'Determining second elimination...',
        'Final champion emerging...'
      ]
    },
    {
      id: 'universe-b',
      title: 'Universe B',
      description: 'Claude eliminated first',
      icon: GitBranchIcon,
      color: 'pink',
      subPhases: [
        'GPT & Gemini critiquing Claude...',
        'Determining second elimination...',
        'Final champion emerging...'
      ]
    },
    {
      id: 'universe-c',
      title: 'Universe C',
      description: 'Gemini eliminated first',
      icon: GitBranchIcon,
      color: 'orange',
      subPhases: [
        'GPT & Claude critiquing Gemini...',
        'Determining second elimination...',
        'Final champion emerging...'
      ]
    },
    {
      id: 'analysis',
      title: 'Multiverse Analysis',
      description: 'Determining the TRUE winner',
      icon: TrophyIcon,
      color: 'yellow',
      subPhases: [
        'Comparing universe outcomes...',
        'Calculating win distributions...',
        'Crowning the multiverse champion...'
      ]
    }
  ], []);

  // Animate dots
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 400);

    return () => clearInterval(interval);
  }, [isActive]);

  // Progress through phases automatically based on estimated timing
  useEffect(() => {
    if (!isActive) {
      setCurrentPhase(0);
      setSubPhase(0);
      return;
    }

    // Phase timing estimates (in ms)
    const phaseDurations = [
      8000,   // Initial battle: ~8s (3 parallel API calls)
      12000,  // Universe A: ~12s (2 sequential rounds)
      12000,  // Universe B: ~12s
      12000,  // Universe C: ~12s
      3000    // Analysis: ~3s
    ];

    const subPhaseDuration = (phaseIndex) => {
      const totalDuration = phaseDurations[phaseIndex];
      const subPhaseCount = phases[phaseIndex].subPhases.length;
      return totalDuration / subPhaseCount;
    };

    let phaseTimer;
    let subPhaseTimer;

    const advanceSubPhase = () => {
      setSubPhase(prev => {
        const maxSubPhases = phases[currentPhase]?.subPhases.length || 1;
        if (prev < maxSubPhases - 1) {
          return prev + 1;
        }
        return prev;
      });
    };

    const advancePhase = () => {
      setCurrentPhase(prev => {
        if (prev < phases.length - 1) {
          setSubPhase(0);
          return prev + 1;
        }
        return prev;
      });
    };

    // Start sub-phase cycling
    subPhaseTimer = setInterval(advanceSubPhase, subPhaseDuration(currentPhase));

    // Schedule phase advance
    phaseTimer = setTimeout(() => {
      advancePhase();
    }, phaseDurations[currentPhase]);

    return () => {
      clearTimeout(phaseTimer);
      clearInterval(subPhaseTimer);
    };
  }, [isActive, currentPhase, phases]);

  // Notify parent of phase changes
  useEffect(() => {
    if (onPhaseChange) {
      onPhaseChange(phases[currentPhase]?.id);
    }
  }, [currentPhase, phases, onPhaseChange]);

  if (!isActive) return null;

  const phase = phases[currentPhase];
  const PhaseIcon = phase?.icon || SparklesIcon;

  const colorClasses = {
    cyan: {
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
      text: 'text-cyan-300',
      icon: 'text-cyan-400',
      glow: 'shadow-cyan-500/20'
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      text: 'text-purple-300',
      icon: 'text-purple-400',
      glow: 'shadow-purple-500/20'
    },
    pink: {
      bg: 'bg-pink-500/10',
      border: 'border-pink-500/30',
      text: 'text-pink-300',
      icon: 'text-pink-400',
      glow: 'shadow-pink-500/20'
    },
    orange: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      text: 'text-orange-300',
      icon: 'text-orange-400',
      glow: 'shadow-orange-500/20'
    },
    yellow: {
      bg: 'bg-yellow-500/10',
      border: 'border-yellow-500/30',
      text: 'text-yellow-300',
      icon: 'text-yellow-400',
      glow: 'shadow-yellow-500/20'
    }
  };

  const colors = colorClasses[phase?.color] || colorClasses.purple;

  return (
    <div className={`mt-4 p-4 rounded-xl border ${colors.bg} ${colors.border} shadow-lg ${colors.glow} transition-all duration-500`}>
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-slate-400 font-medium">Multiverse Progress</span>
          <span className="text-xs text-slate-400">{currentPhase + 1} / {phases.length}</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500 transition-all duration-500 ease-out"
            style={{ width: `${((currentPhase + (subPhase / (phase?.subPhases.length || 1))) / phases.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Current phase info */}
      <div className="flex items-start gap-4">
        {/* Animated icon */}
        <div className={`relative p-3 rounded-xl ${colors.bg} border ${colors.border}`}>
          <PhaseIcon className={`h-6 w-6 ${colors.icon} animate-pulse`} />
          <div className={`absolute inset-0 rounded-xl ${colors.bg} blur-xl opacity-50`} />
        </div>

        {/* Phase details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className={`font-bold ${colors.text}`}>{phase?.title}</h4>
            <Loader2Icon className={`h-4 w-4 ${colors.icon} animate-spin`} />
          </div>
          <p className="text-slate-400 text-sm mb-2">{phase?.description}</p>

          {/* Current sub-phase with animated dots */}
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${colors.text}`}>
              {phase?.subPhases[subPhase]}{dots}
            </span>
          </div>
        </div>
      </div>

      {/* Universe indicators */}
      <div className="mt-4 flex items-center justify-center gap-2">
        {phases.slice(1, 4).map((p, i) => {
          const universeIndex = i + 1;
          const isComplete = currentPhase > universeIndex;
          const isCurrent = currentPhase === universeIndex;
          const isPending = currentPhase < universeIndex;

          return (
            <div
              key={p.id}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-300 ${
                isComplete
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                  : isCurrent
                  ? `${colorClasses[p.color].bg} ${colorClasses[p.color].text} border ${colorClasses[p.color].border} animate-pulse`
                  : 'bg-slate-800/50 text-slate-500 border border-slate-700/30'
              }`}
            >
              <GitBranchIcon className="h-3 w-3" />
              <span>{p.title.replace('Universe ', 'U')}</span>
              {isComplete && <span className="ml-1">✓</span>}
            </div>
          );
        })}
      </div>

      {/* Fun facts / tips while waiting */}
      <div className="mt-4 pt-3 border-t border-slate-700/30">
        <MultiverseFact currentPhase={currentPhase} />
      </div>
    </div>
  );
};

// Component to show fun facts about the multiverse process
const MultiverseFact = ({ currentPhase }) => {
  const facts = [
    "Each universe represents a different 'what if' scenario based on elimination order.",
    "The multiverse winner is the AI that performs best across ALL possible paths.",
    "An 'absolute' winner means one AI won in every single universe - true dominance!",
    "If there's a tie, the highest average final score becomes the tie-breaker.",
    "This costs 7 prompts: 1 shared initial + 2 per universe × 3 universes."
  ];

  const [factIndex, setFactIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setFactIndex(prev => (prev + 1) % facts.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [facts.length]);

  return (
    <div className="flex items-start gap-2">
      <SparklesIcon className="h-4 w-4 text-purple-400 mt-0.5 flex-shrink-0" />
      <p className="text-xs text-slate-400 leading-relaxed animate-fade-in">
        <span className="text-purple-300 font-medium">Did you know? </span>
        {facts[factIndex]}
      </p>
    </div>
  );
};

export default MultiverseProgress;
